import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Typography, CircularProgress } from '@mui/material'
import axios from 'axios'

import Logo from '@/assets/AppLogo.png'
import GraphImage from '@/assets/LoginLightMode.svg'
import GraphImageDark from '@/assets/LoginDarkMode.svg'

import ThemeToggle from '@/components/sidebar/ThemeToggle'
import { useAppTheme } from '@/theme/ThemeContext'
import LoginInput from '@/components/auth/LoginInput'
import { useAuth } from '@/auth/AuthContext'
import { passwordChangeErrorMessage } from '@/services/authErrors'

const API_BASE_URL = import.meta.env.VITE_API_URL

type PasswordRequirement = {
  label: string
  check: (password: string) => boolean
}

const passwordRequirements: PasswordRequirement[] = [
  { label: 'אותיות קטנות', check: (p) => /[a-z]/.test(p) },
  { label: 'אותיות גדולות', check: (p) => /[A-Z]/.test(p) },
  { label: 'מספרים', check: (p) => /[0-9]/.test(p) },
  { label: "תווים [!'#@$%^*]", check: (p) => /[!'#@$%^*]/.test(p) },
  { label: 'מינימום 8 תווים', check: (p) => p.length >= 8 },
]

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate()
  const { mode, toggleTheme } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { loginChallenge } = useAuth()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const validationResults = useMemo(() => {
    return passwordRequirements.map(req => ({
      ...req,
      passed: req.check(newPassword),
    }))
  }, [newPassword])

  const allRequirementsMet = validationResults.every(r => r.passed)
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0

  const canSubmit = currentPassword.length > 0 && allRequirementsMet && passwordsMatch && !isLoading

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return

    if (!loginChallenge) {
      setError('פג תוקף ההתחברות. נא להתחבר מחדש')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await axios.post(`${API_BASE_URL}/api/auth/modify-password`, {
        challenge: loginChallenge,
        oldPassword: currentPassword,
        newPassword,
      })
      setSuccess(true)
      setTimeout(() => {
        navigate('/login', { replace: true })
      }, 2000)
    } catch (err) {
      setError(passwordChangeErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  const colors = {
    background: isDarkMode ? '#0a1628' : '#F5FAFF',
    titleText: isDarkMode ? '#BBD7FF' : '#0B4F6C',
    welcomeText: isDarkMode ? '#7CAD44' : '#003A5D',
    subtitleText: isDarkMode ? '#FE595A' : '#6B7A8C',
    languageText: isDarkMode ? '#94a3b8' : '#4A5C6A',
    buttonBg: isDarkMode ? '#1e3a5f' : '#E3F5FF',
    buttonText: isDarkMode ? '#FFFFFF' : '#1D3981',
    buttonHover: isDarkMode ? '#2d4a6f' : '#B3E0FF',
    inputBg: isDarkMode ? '#0f1f35' : '#FFFFFF',
    inputBorderDefault: isDarkMode ? '#1e3a5f' : '#E0E3E7',
    inputBorderFocus: isDarkMode ? '#7CAD44' : '#4CAF50',
    checkPassed: '#4CAF50',
    checkFailed: isDarkMode ? '#64748b' : '#9E9E9E',
  }

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        bgcolor: colors.background,
        transition: 'background-color 0.3s ease',
      }}
    >
      {/* Left side - decorative (same as Login) */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          px: 8,
          py: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 520,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            height: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src={Logo}
              sx={{ width: '72px', height: '80px', objectFit: 'cover', objectPosition: 'top', borderRadius: '50%' }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: colors.titleText,
                transition: 'color 0.3s ease',
              }}
            >
              Rimonim Tech SmartNet
            </Typography>
          </Box>

          <Box
            component="img"
            src={isDarkMode ? GraphImageDark : GraphImage}
            alt="Water consumption graph"
            sx={{
              width: '80%',
              // borderRadius: 2,
              marginTop: '95px',
              marginLeft: '150px',
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '18px',
              mt: 10,
              position: 'relative',
              right: '150px',
            }}
          >
            {[
              { label: 'פתוח', count: 3, color: '#8577E6', w: 584 },
              { label: 'בתהליך', count: 6, color: '#F79268', w: 484 },
              { label: 'בוצע', count: 32, color: '#46BE8B', w: 344 },
            ].map(card => (
              <Box
                key={card.label}
                sx={{
                  width: card.w,
                  height: 65,
                  backgroundColor: isDarkMode ? '#202B40' : '#FAFBFD',
                  borderRadius: '16px',
                  border: `1px solid ${isDarkMode ? '#374151' : 'rgba(50, 59, 119, 0.10)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  justifyContent: 'flex-end',
                  padding: '16px',
                  filter: 'drop-shadow(0px 8px 20px rgba(13, 75, 120, 0.08))',
                }}
              >
                <Typography sx={{ color: '#8F98A5', fontFamily: 'Rubik', fontSize: 16, fontWeight: 300 }}>
                  {card.count}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: card.color,
                    color: '#EFF2F7',
                    fontFamily: 'Rubik',
                    fontSize: 20,
                    fontWeight: 400,
                    borderRadius: '4.5px',
                    padding: '10px 28px',
                    lineHeight: '24px',
                  }}
                >
                  {card.label}
                </Box>
                <Typography sx={{ color: isDarkMode ? '#FFFFFF' : '#003F61', fontSize: 12 }}>
                  ◄
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              mt: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: colors.languageText, fontWeight: 500, alignItems: 'left', transition: 'color 0.3s ease' }}
            >
              HE
              <ThemeToggle
                isDarkMode={isDarkMode}
                onThemeToggle={toggleTheme}
              />
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Right side - form (same structure as Login) */}
      <Box
        sx={{
          flex: 1,
          maxWidth: 800,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, md: 8 },
          py: 4,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 600,
            direction: 'rtl',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src={Logo}
              alt="Rimonim Tech Logo"
              sx={{ width: '140px', height: '155px', objectFit: 'cover', objectPosition: 'top' }}
            />
          </Box>

          <Typography
            variant="h2"
            sx={{ fontWeight: 700, mb: 1, color: colors.welcomeText, transition: 'color 0.3s ease' }}
          >
            שינוי סיסמה
          </Typography>

          <Typography variant="body1" sx={{ mb: 2, color: colors.subtitleText, transition: 'color 0.3s ease' }}>
            הסיסמה לא בתוקף. נא לבחור סיסמה חדשה
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 4,
              alignItems: { xs: 'stretch', sm: 'flex-start' },
            }}
          >
            {/* Form inputs */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, flex: 1 }}
            >
              <LoginInput 
                label="סיסמה נוכחית"
                type="password"
                value={currentPassword}
                onChange={setCurrentPassword}
                hasError={false}
                borderColors={{
                  default: colors.inputBorderDefault,
                  hover: colors.inputBorderFocus,
                  focus: colors.inputBorderFocus,
                }}
                backgroundColor={colors.inputBg}
                accentColor="#6DBB4C"
                accentWidth={20}
                isDarkMode={isDarkMode}
              />

              <LoginInput
                label="סיסמה חדשה"
                type="password"
                value={newPassword}
                onChange={setNewPassword}
                hasError={false}
                borderColors={{
                  default: colors.inputBorderDefault,
                  hover: colors.inputBorderFocus,
                  focus: colors.inputBorderFocus,
                }}
                backgroundColor={colors.inputBg}
                accentColor="#6DBB4C"
                accentWidth={20}
                isDarkMode={isDarkMode}
              />

              <LoginInput
                label="אימות סיסמה חדשה"
                type="password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                hasError={confirmPassword.length > 0 && !passwordsMatch}
                borderColors={{
                  default: colors.inputBorderDefault,
                  hover: colors.inputBorderFocus,
                  focus: colors.inputBorderFocus,
                }}
                backgroundColor={colors.inputBg}
                accentColor="#6DBB4C"
                accentWidth={20}
                isDarkMode={isDarkMode}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 2,
                }}
              >
                <Box
                  sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}
                >
                  {error && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#E53935',
                        fontSize: '12px',
                        lineHeight: 1.4,
                        textAlign: 'right',
                      }}
                    >
                      {error}
                    </Typography>
                  )}
                  {success && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#4CAF50',
                        fontSize: '12px',
                        lineHeight: 1.4,
                        textAlign: 'right',
                      }}
                    >
                      הסיסמה שונתה בהצלחה!
                      <br />
                      מעביר לדף התחברות...
                    </Typography>
                  )}
                  {confirmPassword.length > 0 && !passwordsMatch && !error && !success && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#E53935',
                        fontSize: '12px',
                        lineHeight: 1.4,
                        textAlign: 'right',
                      }}
                    >
                      הסיסמאות אינן תואמות
                    </Typography>
                  )}
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={!canSubmit}
                  sx={{
                    borderRadius: 1,
                    py: 1,
                    px: 4,
                    ml: 2,
                    mr: 2,
                    mb: 2,
                    right: { xs: 0, sm: 100 },
                    height: '35px',
                    width: '45%',
                    fontWeight: 400,
                    bgcolor: colors.buttonBg,
                    color: colors.buttonText,
                    boxShadow: '0px 1px 2px rgba(0, 130, 200, 0.25)',
                    whiteSpace: 'nowrap',
                    transition: 'background-color 0.3s ease, color 0.3s ease',
                    '&:hover': {
                      bgcolor: colors.buttonHover,
                    },
                  }}
                >
                  {isLoading ? <CircularProgress size={20} color="inherit" /> : 'שינוי סיסמה'}
                </Button>
              </Box>
            </Box>

            {/* Password Requirements - Left side */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                pt: 1,
                minWidth: 160,
              }}
            >
              <Typography
                sx={{
                  color: colors.languageText,
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                הסיסמה החדשה חייבת להכיל:
              </Typography>
              {validationResults.map((req, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: req.passed ? colors.checkPassed : colors.checkFailed,
                      fontSize: '1rem',
                    }}
                  >
                    ✓
                  </Typography>
                  <Typography
                    sx={{
                      color: req.passed ? colors.checkPassed : colors.checkFailed,
                      fontSize: '0.8rem',
                    }}
                  >
                    {req.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default ChangePasswordPage
